import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, Plus, Minus, RotateCcw, Crown, Medal, Check, X, Repeat, Edit2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { TeamAvatar } from "./TeamAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sound effect for points
const playPointSound = (positive = true) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = positive ? 880 : 220;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.15);
  } catch (e) {
    console.log("Audio not supported");
  }
};

const Scoreboard = ({ sessionId, groups, onScoreUpdate, showPointButtons = true }) => {
  const [scoreboard, setScoreboard] = useState([]);
  const [isUpdating, setIsUpdating] = useState({});
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamNameInput, setTeamNameInput] = useState("");

  useEffect(() => {
    const sorted = [...groups]
      .map(g => ({ number: g.number, name: g.name || "", avatar_id: g.avatar_id || "", score: g.score || 0, id: g.id }))
      .sort((a, b) => b.score - a.score);
    setScoreboard(sorted);
  }, [groups]);

  const handleUpdateScore = async (groupNumber, points) => {
    setIsUpdating(prev => ({ ...prev, [groupNumber]: true }));
    try {
      await axios.post(`${API}/sessions/${sessionId}/groups/${groupNumber}/score?points=${points}`);
      playPointSound(points > 0);
      if (onScoreUpdate) onScoreUpdate();
    } catch (error) {
      toast.error("Failed to update score");
    } finally {
      setIsUpdating(prev => ({ ...prev, [groupNumber]: false }));
    }
  };

  const handleResetScores = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/scores/reset`);
      toast.success("All scores reset!");
      if (onScoreUpdate) onScoreUpdate();
    } catch (error) {
      toast.error("Failed to reset scores");
    }
  };

  const handleUpdateTeamName = async (groupNumber) => {
    try {
      await axios.patch(`${API}/sessions/${sessionId}/groups/${groupNumber}/name?name=${encodeURIComponent(teamNameInput)}`);
      toast.success("Team name updated!");
      setEditingTeam(null);
      setTeamNameInput("");
      if (onScoreUpdate) onScoreUpdate();
    } catch (error) {
      toast.error("Failed to update team name");
    }
  };

  const startEditingTeam = (team) => {
    setEditingTeam(team.number);
    setTeamNameInput(team.name || `Team ${team.number}`);
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Crown className="w-5 h-5 text-[#FFD700]" />;
    if (index === 1) return <Medal className="w-5 h-5 text-[#C0C0C0]" />;
    if (index === 2) return <Medal className="w-5 h-5 text-[#CD7F32]" />;
    return <span className="text-[#6B5E7C] font-bold">{index + 1}</span>;
  };

  const getRankStyle = (index) => {
    if (index === 0) return "bg-gradient-to-r from-[#FFD700]/20 to-transparent border-[#FFD700]/30";
    if (index === 1) return "bg-gradient-to-r from-[#C0C0C0]/10 to-transparent border-[#C0C0C0]/20";
    if (index === 2) return "bg-gradient-to-r from-[#CD7F32]/10 to-transparent border-[#CD7F32]/20";
    return "bg-white/5 border-white/10";
  };

  const getTeamDisplayName = (team) => {
    return team.name || `Team ${team.number}`;
  };

  return (
    <div className="glass-heavy rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-[#FFD700]" />
          <h3 className="text-lg font-semibold text-white">Scoreboard</h3>
        </div>
        {showPointButtons && (
          <Button
            onClick={handleResetScores}
            variant="ghost"
            size="sm"
            className="text-[#6B5E7C] hover:text-white"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <AnimatePresence>
          {scoreboard.map((team, index) => (
            <motion.div
              key={team.id}
              layout
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className={`flex items-center gap-3 p-3 rounded-xl border ${getRankStyle(index)}`}
            >
              {/* Rank */}
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                {getRankIcon(index)}
              </div>

              {/* Team Avatar */}
              <TeamAvatar 
                teamNumber={team.number} 
                avatarId={team.avatar_id}
                size="sm"
                showBorder={false}
              />

              {/* Team Name */}
              <div className="flex-1 min-w-0">
                {editingTeam === team.number ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={teamNameInput}
                      onChange={(e) => setTeamNameInput(e.target.value)}
                      className="h-8 text-sm bg-white/10 border-white/20"
                      placeholder="Team name"
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleUpdateTeamName(team.number)}
                    />
                    <Button size="sm" onClick={() => handleUpdateTeamName(team.number)} className="h-8 w-8 p-0 bg-[#00E676]">
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingTeam(null)} className="h-8 w-8 p-0">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold truncate">{getTeamDisplayName(team)}</p>
                    {showPointButtons && (
                      <button onClick={() => startEditingTeam(team)} className="text-[#6B5E7C] hover:text-white">
                        <Edit2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Score */}
              <div className="text-2xl font-bold text-white min-w-[60px] text-center">
                {team.score}
              </div>

              {/* Point Controls */}
              {showPointButtons && (
                <div className="flex gap-1">
                  <Button
                    onClick={() => handleUpdateScore(team.number, -50)}
                    disabled={isUpdating[team.number]}
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 text-[#FF1744] hover:bg-[#FF1744]/20 text-xs"
                    title="Wrong answer (-50)"
                  >
                    -50
                  </Button>
                  <Button
                    onClick={() => handleUpdateScore(team.number, 50)}
                    disabled={isUpdating[team.number]}
                    size="sm"
                    className="h-8 px-2 bg-[#FFC400] hover:bg-[#FFC400]/80 text-black text-xs"
                    title="Steal (+50)"
                  >
                    <Repeat className="w-3 h-3 mr-1" />
                    +50
                  </Button>
                  <Button
                    onClick={() => handleUpdateScore(team.number, 100)}
                    disabled={isUpdating[team.number]}
                    size="sm"
                    className="h-8 px-2 bg-[#00E676] hover:bg-[#00E676]/80 text-black text-xs"
                    title="Correct (+100)"
                  >
                    +100
                  </Button>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Legend */}
      {showPointButtons && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-xs text-[#6B5E7C] mb-2">Point Options:</p>
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="flex items-center gap-1 text-[#00E676]">
              <Check className="w-3 h-3" /> +100 Correct
            </span>
            <span className="flex items-center gap-1 text-[#FFC400]">
              <Repeat className="w-3 h-3" /> +50 Steal
            </span>
            <span className="flex items-center gap-1 text-[#FF1744]">
              <X className="w-3 h-3" /> -50 Wrong
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Participant-facing scoreboard (read-only)
const ParticipantScoreboard = ({ scoreboard, myTeamNumber }) => {
  if (!scoreboard || scoreboard.length === 0) return null;

  const getRankStyle = (index) => {
    if (index === 0) return "bg-gradient-to-r from-[#FFD700]/30 to-transparent border-[#FFD700]/50";
    if (index === 1) return "bg-gradient-to-r from-[#C0C0C0]/20 to-transparent border-[#C0C0C0]/30";
    if (index === 2) return "bg-gradient-to-r from-[#CD7F32]/20 to-transparent border-[#CD7F32]/30";
    return "bg-white/5 border-white/10";
  };

  const getTeamDisplayName = (team) => {
    return team.name || `Team ${team.number}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-heavy rounded-2xl p-6 mb-6"
    >
      <div className="flex items-center gap-3 mb-4">
        <Trophy className="w-6 h-6 text-[#FFD700]" />
        <h3 className="text-lg font-semibold text-white">Scoreboard</h3>
      </div>

      <div className="space-y-2">
        {scoreboard.map((team, index) => {
          const isMyTeam = team.number === myTeamNumber;
          return (
            <motion.div
              key={team.number}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`flex items-center gap-3 p-3 rounded-xl border ${getRankStyle(index)} ${isMyTeam ? 'ring-2 ring-[#E91E8C]' : ''}`}
            >
              {/* Rank */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                index === 0 ? 'bg-[#FFD700] text-black' :
                index === 1 ? 'bg-[#C0C0C0] text-black' :
                index === 2 ? 'bg-[#CD7F32] text-black' :
                'bg-white/10 text-white'
              }`}>
                {index + 1}
              </div>

              {/* Team Avatar */}
              <TeamAvatar 
                teamNumber={team.number} 
                avatarId={team.avatar_id}
                size="sm"
                showBorder={false}
              />

              {/* Team Name */}
              <div className="flex-1">
                <p className={`font-semibold ${isMyTeam ? 'text-[#E91E8C]' : 'text-white'}`}>
                  {getTeamDisplayName(team)} {isMyTeam && '(You)'}
                </p>
              </div>

              {/* Score */}
              <div className="text-2xl font-bold text-white">
                {team.score}
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export { ParticipantScoreboard, playPointSound };
export default Scoreboard;
